<template>
  <div class="nx-central-newsroom-media-taxonomy-excluding-sendings-wrapper"
       :data-vv-name="name"
       :data-vv-scope="scope"
       :data-vv-as="label"
       :error-messages="errorMessages"
       data-vv-validate-on="input|blur"
       data-vv-value-path="value"
       v-validate="validation"
  >
    <div
      class="nx-url-selector input-group v-text-field text--primary mb-4">
      <label class="v-label--active v-label theme--light">
        {{ label | capitalize }}
      </label>
      <nx-taxonomy-selector-by-media
        :allow-only-remote-medias="allowOnlyRemoteMedias"
        class="mt-3 mb-4"
        :label="label"
        :value="includedValue"
        :disabled="disabled"
        :excludedMediaIds="excludedIds"
        v-bind="$attrs"
        :required="required"
        @input="onIncludedInput"
        @blur="onBlur"
        @focus="onFocus"
        :remoteMedias="remoteMedias"
        :allow-only-media-ids="allowOnlyMediaIds"
        :dont-filter-central-newsroom-media-ids="dontFilterCentralNewsroomMediaIds"
      ></nx-taxonomy-selector-by-media>
    </div>

    <v-select
      :items="availableMedias"
      chips
      :value="excludedValue"
      item-text="name"
      item-value="id"
      :label="$t(`message.form.fields.${name}Excluded`)"
      :placeholder="$t('message.taxonomy.selector.selectMedia')"
      multiple
      @input="onExcludedInput"
      return-object
    ></v-select>
  </div>
</template>

<script>
import Storable from '@/mixins/storable';
import NxTaxonomySelectorByMedia from '@/modules/taxonomy/selector/SelectorByMedia';
import FormElement from '@/modules/form/mixins/form-element';
import toast from '@/modules/toast/services';
import {
  get, isEmpty, isObject, uniqBy, isNumber,
} from 'lodash';
import api from '../api';

export default {
  name: 'nx-central-newsroom-media-taxonomy-excluding-sendings',
  mixins: [FormElement, Storable],
  inject: {
    $getParentEditor: { default: null },
  },
  components: {
    NxTaxonomySelectorByMedia,
  },
  props: {
    allowOnlyRemoteMedias: Boolean,
    allowOnlyMediaIds: {
      type: Array,
      default: () => [],
    },
    disableAutomaticExclude: Boolean,
    disableAutomaticInclude: Boolean,
    dontFilterCentralNewsroomMediaIds: {
      type: Array,
      default: () => ([]),
    },
    remoteMedias: {
      type: Array,
      required: false,
      default: () => ([]),
      validator(value) {
        return value.every((remoteMedia) => {
          if (!isObject(remoteMedia)) return 'Remote media is not object';

          if (!['id', 'name', 'type'].every(prop => !isEmpty(remoteMedia[prop]))) return 'Remote media does not have all required props';
          return true;
        });
      },
    },
    value: {
      type: Object,
      required: false,
      default: () => ({}),
      validator(value) {
        if ('included' in value
          && !Array.isArray(value.included)) {
          return false;
        }
        if ('excluded' in value
          && !Array.isArray(value.excluded)) {
          return false;
        }
        return true;
      },
    },
  },
  computed: {
    storePath() {
      return this.$getParentEditor ? this.$getParentEditor().storePath : 'content/editor';
    },
    contentDraft() {
      return this.getters.draft;
    },
    includedValue() {
      return this.value.included || [];
    },
    excludedValue() {
      return this.value.excluded || [];
    },
    excludedIds() {
      return this.excludedValue.map(media => media.id);
    },
    availableMedias() {
      const medias = this.allowOnlyRemoteMedias
        ? []
        : this.$store.getters.availableMedias
          .filter(
            this.allowOnlyMediaIds.length > 0
              ? media => this.allowOnlyMediaIds.includes(media.id)
              : () => true,
          )
          .filter(media => !media.isCentralNewsroom || this.dontFilterCentralNewsroomMediaIds.includes(media.id))
          .map(media => ({ id: media.id, name: media.name, type: media.type || 'media' }));
      return medias
        .concat(this.remoteMedias);
    },
  },
  methods: {
    onIncludedInput(included) {
      if (!this.disableAutomaticExclude
        && this.includedValue.length > included.length) {
        const removed = this.includedValue.filter(taxon => !included.includes(taxon));
        const removedMedias = removed.map(taxon => taxon.media);
        const excluded = this.removeDuplicates(this.excludedValue.concat(removedMedias));
        const excludedIds = excluded.map(media => media.id);
        included = included.filter(taxon => !excludedIds.includes(taxon.media.id));
        this.onInput({ included, excluded });
      } else {
        this.updateIncluded(included);
      }
    },
    async onExcludedInput(excluded) {
      if (excluded.length > this.excludedValue.length) {
        const excludedIds = excluded.map(media => media.id);
        const included = this.includedValue.filter(taxon => !excludedIds.includes(taxon.media.id));
        this.onInput({ included, excluded });
      } else if (!this.disableAutomaticInclude) {
        const taxonomyIds = this.getCurrentTaxonomies();
        const mappedTaxonomies = await this.getMappedTaxonomyIds(taxonomyIds);
        console.log('mappedTaxonomies', mappedTaxonomies);
        const addedMediaIds = this.excludedValue.filter(media => !excluded.includes(media)).map(media => media.id);
        console.log('addedMediaIds', addedMediaIds);
        const addedTaxonomies = mappedTaxonomies.filter(taxon => addedMediaIds.includes(taxon.media.id));
        console.log('addedTaxonomies', addedTaxonomies);
        const included = this.removeDuplicates(this.includedValue.concat(addedTaxonomies));
        console.log('included', included);
        this.onInput({ included, excluded });
      } else {
        this.updateExcluded(excluded);
      }
    },
    onInput(inputValue) {
      this.$parentForm.updateField(this.name, inputValue);
      this.$emit('input', inputValue);
    },
    onBlur($event) {
      this.$parentForm.updateField(this.name, this.value);
      this.$emit('blur', $event);
      this.flags.focused = false;
    },
    updateIncluded(included) {
      const value = {
        included,
        excluded: this.excludedValue,
      };
      this.onInput(value);
    },
    updateExcluded(excluded) {
      const value = {
        included: this.includedValue,
        excluded,
      };
      this.onInput(value);
    },
    removeDuplicates(taxons) {
      return uniqBy(taxons, taxon => taxon.id);
    },
    getCurrentTaxonomies() {
      const getId = taxon => (isNumber(taxon) ? taxon : get(taxon, 'id', null));
      return [
        getId(get(this.contentDraft, 'mainTaxonomyId')),
        ...get(this.contentDraft, 'secondaryTaxonomyIds', []).map(getId),
      ].filter(Boolean);
    },
    async getMappedTaxonomyIds(taxonomyIds) {
      const result = await Promise.all(taxonomyIds.map(async (taxonomyId) => {
        const { mappedTaxonomyIds } = await api.getCentralNewsroomTaxonomyMappings(taxonomyId);
        return mappedTaxonomyIds;
      }));
      return this.removeDuplicates(result.flat());
    },
    async syncWithTaxonomies(taxonomyIds) {
      try {
        const result = await this.getMappedTaxonomyIds(taxonomyIds);

        if (result.length > 0) {
          this.updateIncluded(result);
          toast.success('message.centralNewsroom.taxonomySendings.success');
        }
      } catch (e) {
        toast.notify('message.centralNewsroom.taxonomySendings.error');
        console.error(e);
      }
    },
    subscribeToChannelDraftUpdates() {
      // Subscribe to mutation
      this.$store.subscribe((mutation) => {
        if (mutation.type !== `${this.storeName}/UPDATE_CHANNEL_DRAFT`
          || mutation.payload.channel !== 'master') {
          return;
        }

        console.log('suscripción a la mutación');
        console.log({ ...this.contentDraft });

        const getId = (taxon) => {
          if (isNumber(taxon)) return taxon;
          return get();
        };
        // Sync with payload mainTaxonomyId and draft secondaryTaxonomyIds
        if (Object.keys(mutation.payload.fields).includes('mainTaxonomyId')
          || Object.keys(mutation.payload.fields).includes('secondaryTaxonomyIds')) {
          this.syncWithTaxonomies(this.getCurrentTaxonomies());
        }
      });
    },
  },
  async mounted() {
    this.subscribeToChannelDraftUpdates();
    if (!isEmpty(this.includedValue)) {
      return;
    }

    const mainTaxonomyId = get(this.contentDraft, 'mainTaxonomyId.id');
    if (mainTaxonomyId) {
      this.syncWithTaxonomies([mainTaxonomyId]);
    }
  },
};
</script>
